import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, Spinner } from "@bphxd/ds-core-react";
import { Remove32, Delete48 } from "@bphxd/ds-core-react/lib/icons";
import "./QuestionnairePage.scss";
import { questionnaireFetch } from "../../api/endpoints";
import { useRef } from "react";



type QuestionnairePageProps = {
    initialData?: any;
    token?: string | null;
};

type Question = {
    id: string;
    value: string;
    answer: string | null;
    approved: boolean | null;
    open?: boolean;
    satisfied?: "YES" | "NO" | null;
    remarks?: string;
    isNew?: boolean;
    isSaved?: boolean;
};

type ValidationError = {
    questionId?: string;
    boundary?: boolean;
    message: string;
};

export default function QuestionnairePage({ initialData, token }: QuestionnairePageProps) {
    const location = useLocation();
    const data = initialData || location.state;
    const authtoken = token || sessionStorage.getItem("token");
    const [isLoading, setIsLoading] = useState(true);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [boundaryOpen, setBoundaryOpen] = useState(false);
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const hasFetched = useRef(false);
    const [boundary, setBoundary] = useState({
        answer: "",
        satisfied: "",
        remarks: "",
        conditions: "" as string | string[],
        isSaved: false,
    });

    // CHECK IF FORM IS READY TO SUBMIT
    const isFormValid = () => {
        // Check if at least one question has satisfied/not satisfied status
        const hasQuestionInput = questions.some(q => {
            const hasAnswer = q.answer !== null && q.answer !== "";
            if (!hasAnswer || q.isNew) return false;
            
            // Question must have satisfied status
            if (!q.satisfied) return false;
            
            // If not satisfied, remarks are required
            if (q.satisfied === "NO" && !q.remarks?.trim()) return false;
            
            return true;
        });

        // Check boundary condition (if it exists)
        const hasBoundaryInput = boundary.answer && boundary.satisfied && 
            (boundary.satisfied === "YES" || (boundary.satisfied === "NO" && boundary.remarks?.trim()));

        // At least one must be valid (question or boundary)
        return hasQuestionInput || hasBoundaryInput;
    };

    // VALIDATION FUNCTION
    const validateForm = (): { isValid: boolean; errors: ValidationError[] } => {
        const errors: ValidationError[] = [];

        // Validate boundary condition
        if (boundary.answer) {
            if (!boundary.satisfied) {
                errors.push({ boundary: true, message: "Boundary condition must be marked as Satisfied or Not Satisfied" });
            } else if (boundary.satisfied === "NO" && !boundary.remarks?.trim()) {
                errors.push({ boundary: true, message: "Remarks are required when boundary condition is Not Satisfied" });
            }
        }

        // Validate existing questions (those with answers)
        questions.forEach((q) => {
            const hasAnswer = q.answer !== null && q.answer !== "";
            if (hasAnswer && !q.isNew) {
                if (!q.satisfied) {
                    errors.push({ questionId: q.id, message: `Question "${q.value}" must be marked as Satisfied or Not Satisfied` });
                } else if (q.satisfied === "NO" && !q.remarks?.trim()) {
                    errors.push({ questionId: q.id, message: `Remarks are required for "${q.value}" when marked as Not Satisfied` });
                }
            }
        });

        return { isValid: errors.length === 0, errors };
    };

    // FETCH API
    useEffect(() => {
        if (!data?.order_number) return;
        if (hasFetched.current) return;
        hasFetched.current = true;
        const fetchData = async () => {
            try {
                setIsLoading(true);

                const url = `${questionnaireFetch}?searchField=order_number&searchVal=${data.order_number}&country_name=${data.country_name}`;

                const res = await fetch(url, {
                    method: 'get',
                    headers: {
                        'Content-Type': 'application/json',
                        AuthorizationToken: `Bearer ${authtoken}`
                    },
                });
                const result = await res.json();

                const countryData = result?.data?.[0]?.Country?.[0];

                const mapped =
                    countryData?.Questionaries?.map((q: any) => ({
                        id: q.id,
                        value: q.value,
                        answer: q.answer || null,
                        approved: q.approved || null,
                        open: false,
                        satisfied: null,
                        remarks: "",
                        isSaved: false,
                    })) || [];

                setQuestions(mapped);

                if (countryData?.Boundry_condition) {
                    setBoundary({
                        conditions: countryData.Boundry_condition.value,
                        answer: countryData.Boundry_condition.answer === "true" ? "YES" : countryData.Boundry_condition.answer === "false" ? "NO" : "",
                        satisfied: countryData.Boundry_condition.approved === true
                            ? "YES" : countryData.Boundry_condition.approved === false ? "NO" : "",
                        remarks: countryData.Boundry_condition.remarks || "",
                        isSaved: false,
                    });
                }
            } catch (err) {
                if (err instanceof Error && err.name === 'AbortError') {
                    console.log("Fetch cancelled");
                } else {
                    console.error("API Error:", err);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [data?.order_number, data?.country_name, token]);

    // TOGGLE
    const toggleOpen = (id: string) => {
        setQuestions((prev) =>
            prev.map((q) =>
                q.id === id ? { ...q, open: !q.open } : q
            )
        );
    };

    // UPDATE FIELD
    const updateField = (id: string, key: string, value: any) => {
        setQuestions((prev) =>
            prev.map((q) =>
                q.id === id ? { ...q, [key]: value } : q
            )
        );
        setValidationErrors((prev) => prev.filter(err => err.questionId !== id));
    };

    // SAVE QUESTION
    const handleSave = (id: string) => {
        const q = questions.find((q) => q.id === id);

        if (!q?.value.trim()) {
            alert("Question cannot be empty");
            return;
        }

        setQuestions((prev) =>
            prev.map((q) =>
                q.id === id
                    ? { ...q, isNew: false, isSaved: true, open: true }
                    : q
            )
        );
    };

    const updateBoundary = (key: string, value: any) => {
        setBoundary((prev) => ({
            ...prev,
            [key]: value,
            isSaved: false
        }));
    };

    const handleBoundarySave = () => {
        if (boundary.answer) {
            if (!boundary.satisfied) {
                alert("Boundary must be marked Satisfied/Not Satisfied");
                return;
            }

            if (boundary.satisfied === "NO" && !boundary.remarks?.trim()) {
                alert("Remarks required when Not Satisfied");
                return;
            }
        }

        setBoundary((prev) => ({
            ...prev,
            isSaved: true
        }));

        console.log("Boundary Saved:", boundary);
    };

    // SAVE NEW QUESTION
    const handlenewSave = (id: string) => {
        const q = questions.find((q) => q.id === id);

        if (!q?.value.trim()) {
            alert("Question cannot be empty");
            return;
        }

        setQuestions((prev) =>
            prev.map((q) =>
                q.id === id
                    ? { ...q, isNew: true, isSaved: true }
                    : q
            )
        );
    };

    // ADD QUESTION
    const handleAdd = () => {
        setQuestions((prev) => [
            ...prev,
            {
                id: Date.now().toString(),
                value: "",
                answer: null,
                approved: null,
                isNew: true,
                isSaved: false,
                satisfied: null,
                remarks: "",
            },
        ]);
    };

    // DELETE
    const handleDelete = (id: string) => {
        setQuestions((prev) => prev.filter((q) => q.id !== id));
        setValidationErrors((prev) => prev.filter(err => err.questionId !== id));
    };

    // CLEAR
    const handleClear = () => {
        setQuestions((prev) =>
            prev.map((q) =>
                q.isSaved
                    ? q
                    : { ...q, satisfied: null, remarks: "" }
            )
        );

        setBoundary((prev) =>
            prev.isSaved
                ? prev
                : {
                    ...prev,
                    satisfied: "",
                    remarks: "",
                }
        );
        setValidationErrors([]);
    };

    // SUBMIT WITH VALIDATION
    const handleSubmit = () => {
        const { isValid, errors } = validateForm();

        if (!isValid) {
            setValidationErrors(errors);
            alert("Please fix the validation errors before submitting:\n" + errors.map(e => `• ${e.message}`).join('\n'));
            return;
        }

        const payload = {
            order_number: data.order_number,
            country_name: data.country_name,
            vessel_imo_number: data.vessel_imo_number,
            vessel_name: data.vessel_name,
            customer_code: data.cust_code,
            cust_acct_name: data.cust_acct_name,
            questions: questions.map((q) => ({
                id: q.id,
                value: q.value,
                isNew: q.isNew || false,
                satisfied: q.satisfied,
                remarks: q.remarks,
            })),
            boundary: {
                ...boundary,
                satisfied: boundary.satisfied === "YES",
            },
        };

        console.log("FINAL SUBMIT:", payload);
        alert("Form submitted successfully!");
    };

    if (!data) return <div
        style={{ height: "300px", width: "100%", textAlign: "center" }}
        className="no-Data-div"
    >
        No data available
    </div>;

    if (isLoading) {
        return (
            <div className="loader">
                <Spinner color="brand" />
            </div>
        );
    }

    return (
        <div>
            {/* HEADER */}
            <div className="top-header">
                Order: {data.order_number} | Vessel IMO: {data.vessel_imo_number} |
                Vessel: {data.vessel_name} | Customer Code: {data.cust_code} |
                Customer: {data.cust_acct_name} | Country: {data.country_name}
            </div>

            <div className="page">
                {/* BOUNDARY CONDITION */}
                <div className="card">
                    <div className="header" onClick={() => setBoundaryOpen(!boundaryOpen)}>
                        Boundary Condition
                        <span>{boundaryOpen ? "▲" : "▼"}</span>
                    </div>
                    {boundaryOpen &&
                        (
                            <div className="body">
                                {Array.isArray(boundary.conditions) ?
                                    boundary.conditions.map((c, i) => (<p key={i}>{i + 1}. {c}</p>))
                                    : <p>{boundary.conditions}</p>
                                }

                                {boundary.answer !== "" ? (<>
                                    <div className="radio-group horizontal">
                                        <label>
                                            <input
                                                type="radio"
                                                checked={boundary.answer === "YES"}
                                                disabled={!!boundary.answer}
                                                onChange={() => updateBoundary("satisfied", "YES")}
                                            />
                                            YES, boundary condition met
                                        </label>
                                        <label>
                                            <input
                                                type="radio"
                                                checked={boundary.answer === "NO"}
                                                disabled={!!boundary.answer}
                                                onChange={() => setBoundary((prev) => ({ ...prev, answer: "NO" }))}
                                            />
                                            NO, boundary condition not met
                                        </label>
                                    </div>

                                    <div className="radio-group horizontal">
                                        <label>
                                            <input
                                                type="radio"
                                                checked={boundary.satisfied === "YES"}
                                                onChange={() =>
                                                    updateBoundary("satisfied", "YES")
                                                }
                                            />
                                            Satisfied
                                        </label>

                                        <label>
                                            <input
                                                type="radio"
                                                checked={boundary.satisfied === "NO"}
                                                onChange={() =>
                                                    updateBoundary("satisfied", "NO")
                                                }
                                            />
                                            Not Satisfied
                                        </label>
                                    </div>

                                    {boundary.satisfied === "NO" && (
                                        <textarea
                                            className="remarks"
                                            placeholder="Enter remarks"
                                            value={boundary.remarks}
                                            onChange={(e) => updateBoundary("remarks", e.target.value)}
                                        />
                                    )}

                                    {/* Boundary Validation Error */}
                                    {validationErrors.some(err => err.boundary) && (
                                        <div className="validation-error" style={{ color: 'red', fontSize: '14px', marginTop: '10px' }}>
                                            ⚠️ {validationErrors.find(err => err.boundary)?.message}
                                        </div>
                                    )}

                                    <div className="actions-right">
                                        <Button onClick={handleBoundarySave} disabled={boundary.isSaved}>
                                            Save
                                        </Button>
                                    </div>
                                </>) : (<>No Boundary Condition Available</>)
                                }

                            </div>
                        )}

                </div>

                {/* QUESTIONS */}
                {questions.map((q) => {
                    const hasAnswer = q.answer !== null && q.answer !== "";
                    const questionErrors = validationErrors.filter(err => err.questionId === q.id);

                    return (
                        <div key={q.id} className="card">
                            {q.isNew && !q.isSaved ? (
                                <div className="body">
                                    <h4
                                        style={{
                                            display: "flex",
                                            alignItems: "center"
                                        }}
                                        onClick={() => handleDelete(q.id)}
                                    >
                                        Add your question

                                        <span style={{ marginLeft: "auto", cursor: "pointer" }}>
                                            <Remove32 color="red" />
                                        </span>
                                    </h4>

                                    <div style={{ position: 'relative', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                        <input
                                            className="input"
                                            placeholder="Enter your question"
                                            value={q.value}
                                            onChange={(e) =>
                                                updateField(q.id, "value", e.target.value)
                                            }
                                            style={{ flex: 1 }}
                                        />
                                    </div>

                                    <div className="actions-right">
                                        <Button
                                            disabled={!q.value.trim()}
                                            onClick={() => handlenewSave(q.id)}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="header" onClick={() => toggleOpen(q.id)}>
                                        {q.value || "Question"}

                                        <div style={{ display: "flex", gap: "10px" }}>
                                            {q.isSaved && q.isNew && (
                                                <div onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDelete(q.id);
                                                }}>
                                                    <Delete48 color="red" />
                                                </div>
                                            )}
                                            {!q.isNew && (
                                                <span>{q.open ? "▲" : "▼"}</span>
                                            )}
                                        </div>
                                    </div>

                                    {q.open && !q.isNew && (
                                        <div className="body">
                                            {hasAnswer ? (
                                                <div className="answer">{q.answer}</div>
                                            ) : (
                                                <p>Not Answered Yet</p>
                                            )}

                                            {hasAnswer && (
                                                <>
                                                    <div className="radio-group horizontal">
                                                        <label>
                                                            <input
                                                                type="radio"
                                                                checked={q.satisfied === "YES"}
                                                                onChange={() =>
                                                                    updateField(q.id, "satisfied", "YES")
                                                                }
                                                            />
                                                            Satisfied
                                                        </label>

                                                        <label>
                                                            <input
                                                                type="radio"
                                                                checked={q.satisfied === "NO"}
                                                                onChange={() =>
                                                                    updateField(q.id, "satisfied", "NO")
                                                                }
                                                            />
                                                            Not Satisfied
                                                        </label>
                                                    </div>

                                                    {q.satisfied === "NO" && (
                                                        <textarea
                                                            className="remarks"
                                                            placeholder="Enter remarks"
                                                            value={q.remarks}
                                                            onChange={(e) =>
                                                                updateField(q.id, "remarks", e.target.value)
                                                            }
                                                        />
                                                    )}

                                                    {questionErrors.length > 0 && (
                                                        <div className="validation-error" style={{ color: 'red', fontSize: '14px', marginTop: '10px' }}>
                                                            ⚠️ {questionErrors[0].message}
                                                        </div>
                                                    )}

                                                    <div className="actions-right">
                                                        <Button onClick={() => handleSave(q.id)}>
                                                            Save
                                                        </Button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    );
                })}

                {/* ACTION BUTTONS */}
                <div className="actions">
                    <Button rounded="pill" theme="brand" level="secondary" size="lg" onClick={handleClear}>Clear</Button>
                    <div style={{ marginLeft: "auto" }}>
                        <Button style={{ marginRight: '20px' }} rounded="pill" theme="brand" level="secondary" size="lg" onClick={handleAdd}>Add</Button>
                        <Button 
                            rounded="pill" 
                            theme="brand" 
                            level="primary" 
                            size="lg"
                            onClick={handleSubmit}
                            disabled={!isFormValid()} // ✅ Disable until user provides satisfaction + remarks
                        >
                            Submit
                        </Button>
                    </div>

                </div>
            </div>
        </div>
    );
}